/**
 * Fills — and with `newsletter_autosignup=1` also sends — the Klaviyo form in
 * the New to Altenew signup band.
 *
 * The order confirmation email links to /pages/free-e-book with the shopper's
 * address and `newsletter_autosignup=1`. They already pressed the button in the
 * email, so the band should finish the job rather than ask them to retype an
 * address we were handed and press a second button.
 *
 * assets/newsletter-autosignup.js does this for Shopify's own customer forms,
 * but it can only fill them: this store runs Shopify's CAPTCHA on those, the
 * token is minted by a genuine user submit, and a scripted one comes back as
 * "Missing CAPTCHA token". Klaviyo's embed posts straight to
 * a.klaviyo.com/client/subscriptions/ from its own click handler, with no
 * CAPTCHA in the way, so here the last step really can be taken for them.
 *
 * Loaded only by sections/new-to-altenew-signup.liquid, and only when that band
 * carries a Klaviyo form id. The one auto-send is deliberately limited to the
 * first band on the page — the page carries the same form twice (the slim band
 * under the hero and the closing call to action) and subscribing once is the
 * point. Both are still filled, so whichever the shopper scrolls to is ready.
 */
(function () {
  if (window.__altenewKlaviyoAutosignup) return;
  window.__altenewKlaviyoAutosignup = true;

  // Klaviyo draws this form in about 8 seconds on a cold cache; the band's own
  // fallback covers the wait. Give it comfortably longer than that before
  // giving up, then stop watching so a long-lived tab is not left observing.
  var WATCH_MS = 20000;
  var BAND = '.nta-signup [data-newsletter-guard]';

  function query() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
  }

  var params = query();
  if (!params) return;

  var raw = params.get('email');
  if (!raw) return;

  // The value arrives from a URL, so treat it as untrusted: only a plausible
  // single address gets anywhere near the field. Same test as the sitewide
  // script, deliberately — one link feeds both.
  var email = raw.trim();
  if (email.length > 254) return;
  if (!/^[^\s@,;<>"']+@[^\s@,;<>"']+\.[^\s@,;<>"']+$/.test(email)) return;

  var autoSend = params.get('newsletter_autosignup') === '1';
  var sent = false;

  // React owns the field's value, so assigning to `input.value` is reverted on
  // the next render and the address never reaches Klaviyo's state. Going
  // through the native setter and then dispatching `input` is what React reads.
  var nativeValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');

  function setValue(input, value) {
    if (nativeValue && nativeValue.set) {
      nativeValue.set.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** The email step of the embed in `band`, once Klaviyo has drawn it. */
  function step(band) {
    var embed = band.querySelector('[data-newsletter-embed]');
    if (!embed) return null;

    var input = embed.querySelector('input[type="email"], input[name="email"]');
    // No field means Klaviyo has not drawn the form yet, or has moved past the
    // email step — already subscribed, or showing its success notice. Both are
    // states to leave alone.
    if (!input) return null;

    // Hidden means the guard handed the band to the fallback instead. Filling
    // or clicking something off screen would be worse than doing nothing.
    if (!input.offsetParent) return null;

    var scope = input.form || embed;
    return { input: input, button: scope.querySelector('button') };
  }

  function handle(band, isFirst) {
    if (band.hasAttribute('data-autosignup-done')) return true;

    var found = step(band);
    if (!found) return false;

    // Anything already in the box is the shopper's own: a logged-in address, an
    // autofill, or something they started typing. It wins over the link.
    if (!found.input.value) setValue(found.input, email);

    band.setAttribute('data-autosignup-done', '');

    if (autoSend && isFirst && !sent && found.button) {
      sent = true;
      // A scripted click is untrusted, which stops Shopify's CAPTCHA cold but
      // is fine here: Klaviyo submits from a plain click handler, so this runs
      // the same path as a tap and the form shows its own success notice.
      found.button.click();
    }

    return true;
  }

  function sweep() {
    var bands = document.querySelectorAll(BAND);
    var settled = bands.length > 0;

    Array.prototype.forEach.call(bands, function (band, index) {
      if (!handle(band, index === 0)) settled = false;
    });

    return settled;
  }

  function start() {
    if (sweep()) return;
    if (!('MutationObserver' in window)) return;

    var observer = new MutationObserver(function () {
      if (sweep()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    setTimeout(function () {
      observer.disconnect();
    }, WATCH_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
